import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CadastroEquipe.module.css';
import PageHeader from '../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import CustomAlert from '../../components/CustomAlert/CustomAlert'; // Adicionado
import axios from 'axios';
import { FiCheck, FiTruck, FiAlertCircle, FiSave } from 'react-icons/fi';
import { API_BASE_URL } from '../../config';

const CadastroEquipe = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nomeEquipe, setNomeEquipe] = useState('');
  const [veiculos, setVeiculos] = useState([]);
  const [selectedVeiculos, setSelectedVeiculos] = useState([]);

  // ESTADO: CustomAlert
  const [alert, setAlert] = useState({
      show: false,
      type: 'info',
      title: '',
      text: '',
      showCancelButton: false,
      onConfirm: null,
      onCancel: () => setAlert(prev => ({ ...prev, show: false })), // Função padrão para fechar
      autoClose: 0,
  });
  
  // FUNÇÃO: Disparar o alerta (Ajustada para fechar corretamente)
  const showAlert = useCallback((options) => {
      setAlert({
          show: true,
          type: options.icon,
          title: options.title,
          text: options.text,
          showCancelButton: options.showCancelButton || false,
          
          // Se não houver confirmAction, usa a ação padrão de fechar.
          onConfirm: options.confirmAction || options.cancelAction || (() => setAlert(prev => ({ ...prev, show: false }))),
          onCancel: options.cancelAction || (() => setAlert(prev => ({ ...prev, show: false }))),
          
          autoClose: options.autoClose || 0,
      });
  }, []);

  useEffect(() => {
    const fetchVeiculos = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
            throw new Error("Usuário não autenticado.");
        }

        const [response] = await Promise.all([
            axios.get(`${API_BASE_URL}/api/veiculos`, {
                headers: { Authorization: `Bearer ${token}` }
            }),
            new Promise(resolve => setTimeout(resolve, 800))
        ]);

        setVeiculos(response.data);
        setError('');
      } catch (error) {
        console.error(error);
        
        // Uso do CustomAlert para erros de carregamento
        showAlert({
            icon: 'error',
            title: 'Erro de Carregamento',
            text: 'Não foi possível carregar a lista de veículos. Verifique a conexão.',
        });
        
        if (error.response) {
            setError(`Erro: ${error.response.status} - ${error.response.data.error || 'Erro desconhecido'}`);
        } else if (error.request) {
            setError("Erro de conexão: O servidor não respondeu.");
        } else {
            setError(error.message);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchVeiculos();
  }, [showAlert]); // Adiciona showAlert como dependência

  const toggleVeiculo = (id) => {
    if (selectedVeiculos.includes(id)) {
      setSelectedVeiculos(selectedVeiculos.filter(vId => vId !== id));
    } else {
      setSelectedVeiculos([...selectedVeiculos, id]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // CORREÇÃO: Validação de Nome - Usando CustomAlert e RETURN
    if (!nomeEquipe.trim()) {
        showAlert({
            icon: 'warning',
            title: 'Atenção',
            text: 'O nome da equipe é obrigatório!',
        });
        return; // Sai da função para evitar o travamento
    }
    
    // CORREÇÃO: Validação de Veículos - Usando CustomAlert e RETURN
    if (selectedVeiculos.length === 0) {
        showAlert({
            icon: 'warning',
            title: 'Atenção',
            text: 'Selecione pelo menos um veículo para a equipe!',
        });
        return; // Sai da função para evitar o travamento
    }
    
    setLoading(true);
    try {
        const token = localStorage.getItem('token');
        
        // Simulação de espera e chamada API
        const [response] = await Promise.all([
            axios.post(`${API_BASE_URL}/api/colheita/equipes`, {
                nome: nomeEquipe,
                veiculosIds: selectedVeiculos
            }, {
                headers: { Authorization: `Bearer ${token}` }
            }),
            new Promise(resolve => setTimeout(resolve, 800))
        ]);
        
        const novaEquipeId = response.data.id; // Supondo que a API retorne o ID
        
        // Substituindo alert() por CustomAlert de Sucesso
        showAlert({
            icon: 'success',
            title: 'Sucesso!',
            text: 'Equipe cadastrada com sucesso!',
            autoClose: 1500,
            
            // Ação: Redireciona para os detalhes da nova equipe, não para a home
            confirmAction: () => navigate(`/lista-equipes/${novaEquipeId}`) 
        });
        
        
    } catch (error) {
        console.error(error);
        let errorMsg = "Erro ao cadastrar equipe.";
        if (error.response?.data?.error) {
             errorMsg = error.response.data.error;
        }

        // Substituindo alert() por CustomAlert de Erro
        showAlert({
            icon: 'error',
            title: 'Falha no Cadastro',
            text: errorMsg,
        });

    } finally {
        setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {loading && <LoadingOverlay text="Carregando dados..." />}
      
      <PageHeader 
        title="Nova Equipe de Colheita" 
        subtitle="Defina o nome da equipe e selecione os maquinários."
        backTo="/sistema-colheita"
      />

      <div className={styles.formContainer}>
        {error && (
            <div style={{ padding: '1rem', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FiAlertCircle size={24} />
                <span>{error}</span>
            </div>
        )}

        <div className={styles.inputGroup}>
            <label>Nome da Equipe</label>
            <input 
                type="text" 
                placeholder="Ex: Equipe Alpha - Soja"
                value={nomeEquipe}
                onChange={(e) => setNomeEquipe(e.target.value)}
            />
        </div>

        <div className={styles.sectionTitle}>
            <FiTruck /> Selecionar Veículos e Maquinários
        </div>

        {!loading && !error && veiculos.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                <p>Nenhum veículo disponível para seleção.</p>
            </div>
        )}

        <div className={styles.gridVeiculos}>
            {veiculos.map(veiculo => (
                <div 
                    key={veiculo.id} 
                    className={`${styles.cardVeiculo} ${selectedVeiculos.includes(veiculo.id) ? styles.selected : ''}`}
                    onClick={() => toggleVeiculo(veiculo.id)}
                >
                    <div className={styles.cardHeader}>
                        <span className={styles.tipo}>{veiculo.tipoVeiculo}</span>
                        {selectedVeiculos.includes(veiculo.id) && <FiCheck className={styles.checkIcon} />}
                    </div>
                    <h3>{veiculo.modelo}</h3>
                    <p>{veiculo.placa || veiculo.chassi || 'S/ Placa'}</p>
                    <span className={styles.marca}>M{veiculo.marca}</span>
                </div>
            ))}
        </div>

        <div className={styles.footerActions}>
            <button className={styles.btnCancel} onClick={() => navigate('/sistema-colheita')}>Cancelar</button>
            <button className={styles.btnSave} onClick={handleSubmit} disabled={loading}><FiSave /> Criar Equipe</button>
        </div>
      </div>

      {/* RENDERIZAÇÃO DO CUSTOM ALERT */}
      <CustomAlert 
          show={alert.show}
          type={alert.type}
          title={alert.title}
          text={alert.text}
          showCancelButton={alert.showCancelButton}
          onConfirm={alert.onConfirm}
          onCancel={alert.onCancel}
          autoClose={alert.autoClose}
      />
    </div>
  );
};

export default CadastroEquipe;